import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { DatePicker, Input, Button, Tag, Modal, Card } from "antd";
import {
    SearchOutlined,
    ReloadOutlined,
    DownloadOutlined,
    EyeOutlined,
    FilterOutlined
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { getRequest } from "../../../Utils/AppApi";
import moment from "moment";

const { RangePicker } = DatePicker;

const LIST_DEACTIVATED_LOGS = "/admin/deactivated-user/list";
const EXPORT_DEACTIVATED_LOGS = "/admin/deactivated-users-export";

const DeactivatedUserHistoryPage = ({ history }) => {

    const [isLoading, setLoading] = useState(false);
    const [getData, setData] = useState([]);
    const [getTotalRecord, setTotalRecord] = useState(0);
    const [page, setPage] = useState(1);
    const [isLimit, setLimit] = useState(50);

    const [searchUsername, setSearchUsername] = useState("");
    const [dateRange, setDateRange] = useState([null, null]);

    const [selectedLog, setSelectedLog] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);

    const buildParams = () => {
        const params = { page, limit: isLimit };

        if (searchUsername.trim()) params.username = searchUsername.trim();

        if (dateRange && dateRange[0]) {
            params.startDate = moment(dateRange[0]).format("YYYY-MM-DD");
        }
        if (dateRange && dateRange[1]) {
            params.endDate = moment(dateRange[1]).format("YYYY-MM-DD");
        }

        return new URLSearchParams(params).toString();
    };

    const getDataReports = () => {
        setLoading(true);
        getRequest(`${LIST_DEACTIVATED_LOGS}?${buildParams()}`, history)
            .then((response) => {
                if (response.success) {
                    setData(response.data?.records || []);
                    setTotalRecord(response.data?.total || 0);
                } else {
                    setData([]);
                }
                setLoading(false);
            })
            .catch(() => setLoading(false));
    };

    const handleExport = () => {
        setLoading(true);
        getRequest(`${EXPORT_DEACTIVATED_LOGS}?${buildParams()}`, history)
            .then((response) => {
                if (response.success && response.data?.length) {
                    const headers = [
                        "Username",
                        "Provider Name",
                        "Outlet/Api ID",
                        "Message",
                        "Date & Time"
                    ];

                    const rows = [headers.join(",")];

                    response.data.forEach(item => {
                        rows.push([
                            `"${item.username}"`,
                            `"${item.providerName}"`,
                            `"${item.apiId}"`,
                            `"${item.message.replace(/"/g, '""')}"`,
                            `"${moment(item.createdAt).format("DD/MM/YYYY HH:mm:ss")}"`
                        ].join(","));
                    });

                    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
                    const link = document.createElement("a");
                    link.href = URL.createObjectURL(blob);
                    link.download = `deactivated-user-report-${moment().format("DD-MM-YYYY-HH-mm")}.csv`;
                    link.click();

                    toast.success("Exported successfully");
                } else {
                    toast.info("No data to export");
                }
                setLoading(false);
            })
            .catch(() => setLoading(false));
    };

    const columns = [
        {
            name: "Username",
            selector: row => row.username,
            cell: row => (
                <Tag color="blue">{row.username}</Tag>
            ),
            sortable: true,
            width: "150px"
        },
        {
            name: "Service",
            selector: row => row.providerName,
            sortable: true,
            width: "180px"
        },
        {
            name: "Outlet/Api ID",
            selector: row => row.apiId,
            sortable: true,
            width: "180px"
        },
        {
            name: "Message",
            selector: row => row.message,
            cell: row => (
                <div style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "350px"
                }}>
                    {row.message}
                </div>
            ),
            sortable: true,
            width: "350px"
        },
        {
            name: "Date & Time",
            selector: row => row.createdAt,
            cell: row => (
                moment(row.createdAt).format("DD/MM/YYYY HH:mm:ss")
            ),
            sortable: true,
            width: "180px"
        },
        {
            name: "",
            cell: row => (
                <Button
                    type="text"
                    icon={<EyeOutlined />}
                    onClick={() => {
                        setSelectedLog(row);
                        setIsModalVisible(true);
                    }}
                />
            ),
            width: "80px"
        }
    ];

    useEffect(() => {
        getDataReports();
    }, [page, isLimit]);

    return (
        <div style={{ background: "#f8f9fa", minHeight: "100vh", padding: "20px" }}>
            <div style={{ maxWidth: "1400px", margin: "0 auto" }}>

                <h1 style={{ fontSize: "24px", fontWeight: "600" }}>
                    Deactivated API/Outlet History
                </h1>

                {/* Filters */}
                <Card style={{ margin: "20px 0" }}>
                    <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                        <Input
                            placeholder="Search Username"
                            value={searchUsername}
                            onChange={(e) => setSearchUsername(e.target.value)}
                            style={{ width: "200px" }}
                        />
                        <RangePicker value={dateRange} onChange={setDateRange} />
                        <Button icon={<SearchOutlined />} type="primary" onClick={getDataReports}>
                            Search
                        </Button>
                        <Button icon={<ReloadOutlined />} onClick={() => {
                            setSearchUsername("");
                            setDateRange([null, null]);
                            setPage(1);
                        }}>
                            Clear
                        </Button>
                        <Button icon={<DownloadOutlined />} onClick={handleExport}>
                            Export
                        </Button>
                    </div>
                </Card>

                {/* Table */}
                <Card>
                    <DataTable
                        columns={columns}
                        data={getData}
                        progressPending={isLoading}
                        pagination
                        paginationServer
                        paginationTotalRows={getTotalRecord}
                        paginationPerPage={isLimit}
                        onChangePage={setPage}
                        onChangeRowsPerPage={setLimit}
                        dense
                        highlightOnHover
                    />
                </Card>

                {/* Modal */}
                <Modal
                    title="Full Response Details"
                    open={isModalVisible}
                    onCancel={() => setIsModalVisible(false)}
                    footer={null}
                    width={700}
                >
                    {selectedLog && (
                        <pre style={{
                            background: "#f4f6f8",
                            padding: "16px",
                            borderRadius: "6px",
                            maxHeight: "400px",
                            overflow: "auto"
                        }}>
                            {JSON.stringify(selectedLog.response, null, 2)}
                        </pre>
                    )}
                </Modal>

            </div>
        </div>
    );
};

export default DeactivatedUserHistoryPage;